import {useState, useCallback, useEffect} from 'react'
import {getListDesignLibrary} from '../../../../../services/ArtworkTemplateServices'
import {toaster} from '../../../../../shared/PFToast'

const DEFAULT_PARAMS = {
    name: '',
    sort_key: 'created_at',
    sort_order: 'desc',
    page: 1,
    limit: 20,
}

/**
 * Hook to fetch design library list with server-side pagination.
 * @returns {{ list: object[], total: number, hasMore: boolean, loading: boolean, error: Error | null, setFilters: Function, fetchNextPage: Function }}
 */
export function useListDesignLibrary() {
    const [list, setList] = useState([])
    const [total, setTotal] = useState(0)
    const [pages, setPages] = useState(0)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [params, setParamsState] = useState(DEFAULT_PARAMS)

    const fetchPage = useCallback(
        async (pageNum = 1, append = false) => {
            setLoading(true)
            setError(null)
            try {
                const {success, data, message} = await getListDesignLibrary({...params, page: pageNum})
                if (!success) throw new Error(message || 'Failed to load design library')
                const {images = [], pages: totalPages, total: totalCount} = data
                setTotal(totalCount ?? 0)
                setPages(totalPages ?? 0)
                setHasMore(pageNum < (totalPages ?? 0))
                setList((prev) => (append ? [...prev, ...images] : images))
            } catch (err) {
                setError(err)
                if (!append) setList([])
                toaster({type: 'error', message: err?.message || 'Failed to load library'})
            } finally {
                setLoading(false)
            }
        },
        [params.name, params.sort_key, params.sort_order, params.limit],
    )

    useEffect(() => {
        fetchPage(1, false)
    }, [params.name, params.sort_key, params.sort_order])

    const setFilters = useCallback((next) => {
        setParamsState((prev) => ({...prev, ...next, page: 1}))
    }, [])

    const fetchNextPage = useCallback(async () => {
        const nextPage = params.page + 1
        if (nextPage > pages || loading) return
        await fetchPage(nextPage, true)
        setParamsState((prev) => ({...prev, page: nextPage}))
    }, [params.page, pages, loading, fetchPage])

    return {
        list,
        total,
        hasMore,
        loading,
        error,
        setFilters,
        fetchNextPage,
    }
}
